import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, Search, Plus, Trash2, MessageCircle, ChevronLeft, ChevronRight, X, Loader2, Clock } from "lucide-react";

const Appointments = () => {
  const { doctor } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [all, setAll] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = all ? {} : { date };
      const res = await api.get("/appointments", { params });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, all]);

  const shift = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
    setAll(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${id}`);
    setItems((x) => x.filter((a) => a.id !== id));
  };

  const waSend = (a) => {
    const msg = `Hi ${a.patient_name},\n\nReminder: Your appointment with ${doctor?.name} is on ${a.date} at ${a.time}${a.reason ? `\nReason: ${a.reason}` : ""}\n\nThank you.`;
    const digits = (a.patient_phone || "").replace(/\D/g, "");
    const withCC = digits.length === 10 ? `91${digits}` : digits;
    window.open(`https://wa.me/${withCC}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointments</h1>
          <p className="text-slate-500 text-sm mt-1">Schedule and track patient visits</p>
        </div>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white px-4 py-2.5 rounded-md text-sm font-semibold">
          <Plus size={16} /> New Appointment
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5 flex flex-wrap items-center gap-3">
        <button onClick={() => setAll(true)} className={`px-3 py-1.5 text-sm rounded ${all ? "bg-[#0F2A5F] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>All</button>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft size={16} /></button>
          <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setAll(false); }} className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
          <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-slate-100"><ChevronRight size={16} /></button>
        </div>
        {!all && <button onClick={() => { setDate(new Date().toISOString().slice(0, 10)); }} className="text-xs text-[#0F2A5F] hover:underline">Today</button>}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Calendar size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No appointments for this view</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((a) => (
              <li key={a.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-[#E6F7F5] text-[#0F2A5F] flex flex-col items-center justify-center flex-shrink-0">
                    <Clock size={16} />
                    <span className="text-[10px] font-semibold mt-0.5">{a.time}</span>
                  </div>
                  <div>
                    <button onClick={() => nav(`/app/patients/${a.patient_id}`)} className="font-semibold text-slate-800 hover:text-[#0F2A5F] text-left">
                      {a.patient_name}
                    </button>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.date} • UHID {a.patient_uhid} • {a.reason || "General"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => waSend(a)} title="WhatsApp" className="p-2 text-[#25D366] hover:bg-green-50 rounded">
                    <MessageCircle size={16} />
                  </button>
                  <button onClick={() => del(a.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showNew && <NewAppointmentModal doctor={doctor} onClose={() => setShowNew(false)} onCreated={(a) => setItems((prev) => [a, ...prev])} />}
    </div>
  );
};

const NewAppointmentModal = ({ doctor, onClose, onCreated }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "10:00", reason: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [waPrompt, setWaPrompt] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try {
        const res = await api.get("/patients", { params: { q } });
        setResults(res.data.slice(0, 8));
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { setErr("Select a patient first"); return; }
    setErr("");
    setBusy(true);
    try {
      const res = await api.post("/appointments", { patient_id: selected.id, ...form });
      onCreated?.(res.data);
      const digits = (selected.phone || "").replace(/\D/g, "");
      const withCC = digits.length === 10 ? `91${digits}` : digits;
      const msg = `Hi ${selected.name},\n\nYour appointment with ${doctor?.name}${doctor?.clinic_name ? ` (${doctor.clinic_name})` : ""} is confirmed.\n\nDate: ${form.date}\nTime: ${form.time}${form.reason ? `\nReason: ${form.reason}` : ""}\n\nThank you.`;
      setWaPrompt({ url: `https://wa.me/${withCC}?text=${encodeURIComponent(msg)}`, msg });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to book");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">New Appointment</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {waPrompt ? (
          <div className="px-6 py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto flex items-center justify-center mb-3"><MessageCircle size={28} /></div>
            <h4 className="font-semibold text-slate-800 mb-1">Appointment Booked</h4>
            <p className="text-sm text-slate-500 mb-4">Send confirmation to <span className="font-medium">{selected.name}</span>?</p>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-left text-sm text-slate-700 whitespace-pre-line mb-4">{waPrompt.msg}</div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Skip</button>
              <a href={waPrompt.url} target="_blank" rel="noopener noreferrer" onClick={onClose} className="px-4 py-2 text-sm bg-[#25D366] hover:bg-[#1EBA57] text-white rounded-md font-medium inline-flex items-center gap-2">
                <MessageCircle size={14} /> Send WhatsApp
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Patient</label>
              {selected ? (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-sm">
                    <span className="font-medium text-slate-800">{selected.name}</span>
                    <span className="text-slate-500"> • UHID {selected.uhid} • {selected.phone}</span>
                  </div>
                  <button type="button" onClick={() => { setSelected(null); setQ(""); }} className="text-xs text-[#0F2A5F] hover:underline">Change</button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / phone / UHID" className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
                  {results.length > 0 && (
                    <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-y-auto z-10">
                      {results.map((p) => (
                        <li key={p.id}>
                          <button type="button" onClick={() => { setSelected(p); setResults([]); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                            <div className="font-medium text-slate-800">{p.name}</div>
                            <div className="text-xs text-slate-500">UHID {p.uhid} • {p.phone}</div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                <input type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                <input type="time" required value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
              <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Cleaning, Follow-up" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>

            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button type="submit" disabled={busy || !selected} className="px-4 py-2 text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-60 text-white rounded-md font-medium inline-flex items-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Book
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Appointments;
